import { atom, useAtomValue, useSetAtom } from 'jotai';
import { createContext, type FC, type PropsWithChildren, useContext, useRef } from 'react';
import { type SortInfo } from '~/utils/jsonapi-query';

function readFromStorage(key: string, fallback: SortInfo | null): SortInfo | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'sort' in parsed &&
      typeof parsed.sort === 'string' &&
      'direction' in parsed &&
      (parsed.direction === 'asc' || parsed.direction === 'desc')
    ) {
      return parsed as SortInfo;
    }
    return fallback;
  } catch {
    return fallback;
  }
}

function createSortingAtoms(defaultSorting?: SortInfo, storageKey?: string) {
  const initial = storageKey ? readFromStorage(storageKey, defaultSorting ?? null) : (defaultSorting ?? null);
  const baseAtom = atom<SortInfo | null>(initial);

  const sortingAtom = atom(
    (get) => get(baseAtom),
    (_get, set, value: SortInfo) => {
      set(baseAtom, value);
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(value));
        } catch {
          // Ignore storage errors (e.g. quota exceeded)
        }
      }
    },
  );

  const setSortingAtom = atom(null, (get, set, field: string) => {
    const current = get(baseAtom);
    if (field === current?.sort) {
      set(sortingAtom, { sort: current.sort, direction: current.direction === 'asc' ? 'desc' : 'asc' });
    } else {
      set(sortingAtom, { sort: field, direction: current?.direction ?? 'desc' });
    }
  });

  const setSortingWithDirectionAtom = atom(null, (_get, set, sorting: SortInfo) => {
    set(sortingAtom, sorting);
  });

  return { setSortingAtom, setSortingWithDirectionAtom, sortingAtom };
}

type SortingAtoms = ReturnType<typeof createSortingAtoms>;

const TableSortingContext = createContext<SortingAtoms | null>(null);

function useSortingContext() {
  const ctx = useContext(TableSortingContext);
  if (!ctx) throw new Error('useSorting must be used inside <TableSortingProvider>');
  return ctx;
}

interface TableSortingProviderProps {
  defaultSorting?: SortInfo;
  storageKey?: string;
}

export const TableSortingProvider: FC<PropsWithChildren<TableSortingProviderProps>> = ({
  children,
  defaultSorting,
  storageKey,
}) => {
  const atomsRef = useRef<ReturnType<typeof createSortingAtoms> | null>(null);
  if (!atomsRef.current) {
    atomsRef.current = createSortingAtoms(defaultSorting, storageKey);
  }
  return <TableSortingContext.Provider value={atomsRef.current}>{children}</TableSortingContext.Provider>;
};

export const useSorting = <T extends string = never>(): [T] extends [never] ? SortInfo | null : SortInfo<T> => {
  const { sortingAtom } = useSortingContext();
  return useAtomValue(sortingAtom) as [T] extends [never] ? SortInfo | null : SortInfo<T>;
};

export const useSetSorting = () => {
  const { setSortingAtom } = useSortingContext();
  return useSetAtom(setSortingAtom);
};

export const useSetSortingWithDirection = () => {
  const { setSortingWithDirectionAtom } = useSortingContext();
  return useSetAtom(setSortingWithDirectionAtom);
};
